import bill from '../assets/bill.jpg';
import stamp from '../assets/stamp.jpg';

const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (character) => ({
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;'
}[character]));

const formatAmount = (value) => (Number(value) || 0).toFixed(2);

const transliterateToArabic = (value) => {
  const map = {
    a: 'ا', b: 'ب', c: 'ك', d: 'د', e: 'ي', f: 'ف', g: 'ج', h: 'ه',
    i: 'ي', j: 'ج', k: 'ك', l: 'ل', m: 'م', n: 'ن', o: 'و', p: 'ب',
    q: 'ق', r: 'ر', s: 'س', t: 'ت', u: 'و', v: 'ف', w: 'و', x: 'كس',
    y: 'ي', z: 'ز', ',': '،', '.': '.', '-': '-'
  };
  return Array.from(String(value ?? '')).map((character) => {
    if (/[0-9\s]/.test(character)) return character;
    return map[character.toLowerCase()] || character;
  }).join('');
};

const formatDate = (value) => value ? new Date(`${value}T00:00:00`).toLocaleDateString('en-GB') : '';

const arabicCountry = (value) => (
  /saudi arabia|kingdom of saudi arabia|ksa/i.test(String(value || ''))
    ? 'المملكة العربية السعودية'
    : /india/i.test(String(value || '')) ? 'الهند' : transliterateToArabic(value)
);

export const buildQuotePrintHtml = (quote) => {
  const companyValues = quote.companyDetails || {};
  const client = quote.client || {};
  const quoteDetails = quote.quoteDetails || {};
  const products = (quote.products || []).map((product) => {
    const subtotal = Number(product.subtotal) || (Number(product.unitRate) || 0) * (Number(product.quantity) || 0);
    const vat = Number(product.vat) || subtotal * (Number(product.vatRate) || 0) / 100;
    return {
      ...product,
      subtotal,
      vat,
      total: Number(product.total) || subtotal + vat
    };
  });
  const totals = quote.totals || products.reduce((result, product) => ({
    subtotal: result.subtotal + product.subtotal,
    vat: result.vat + product.vat,
    total: result.total + product.total
  }), { subtotal: 0, vat: 0, total: 0 });
  const detailsTable = (rows) => rows.map(([englishLabel, englishValue, arabicValue, arabicLabel]) => `
    <tr>
      <th class="col2">${escapeHtml(englishLabel)}:</th>
      <td colspan="3" class="col2">${escapeHtml(englishValue || '')}</td>
      <td colspan="3" class="col2 arabic" dir="rtl">${escapeHtml(arabicValue || '')}</td>
      <th class="col2 align-end">${escapeHtml(arabicLabel)}</th>
    </tr>
  `).join('');
  const companyRows = detailsTable([
    ['Name', companyValues['Company Name'] || 'LAR Contracting Company', 'شركة إل آر كونتراكتنج', 'الاسم:'],
    ['Short Address', companyValues['Short Address'] || 'ENAA8599', companyValues['Short Address'] || 'ENAA8599', 'العنوان المختصر:'],
    ['Secondary No', companyValues['Secondary No'] || '2287', companyValues['Secondary No'] || '2287', 'صندوق البريد:']
  ]) + `
    <tr>
      <th class="col2">Building No:</th><td>${escapeHtml(companyValues['Building No'] || '8599')}</td>
      <th class="col2">City:</th><td>${escapeHtml(companyValues.City || 'Prince Saud Ibn Abdullah Ibn Jalawi Branch, Al Jubail')}</td>
      <td class="arabic" dir="rtl">فرع الأمير سعود بن عبدالله بن جلوي، الجبيل</td>
      <th class="col2 align-end">المدينة:</th>
      <td class="arabic" dir="rtl">${escapeHtml(companyValues['Building No'] || '8599')}</td>
      <th class="col2 align-end">رقم المبنى:</th>
    </tr>
    <tr>
      <th class="col2">Customer Account:</th><td>${escapeHtml(companyValues['Customer Account'] || '31335115107')}</td>
      <th class="col2">District:</th><td>${escapeHtml(companyValues.District || 'Tebah Dist.')}</td>
      <td class="arabic" dir="rtl">حي طيبة</td><th class="col2 align-end">الحي:</th>
      <td class="arabic" dir="rtl">${escapeHtml(companyValues['Customer Account'] || '31335115107')}</td>
      <th class="col2 align-end">رقم حساب العميل:</th>
    </tr>
    <tr>
      <th class="col2">Postal Code:</th><td>${escapeHtml(companyValues['Postal Code'] || '35513')}</td>
      <th class="col2">Country:</th><td>${escapeHtml(companyValues.Country || 'Kingdom of Saudi Arabia')}</td>
      <td class="arabic" dir="rtl">${arabicCountry(companyValues.Country || 'Kingdom of Saudi Arabia')}</td>
      <th class="col2 align-end">الدولة:</th>
      <td class="arabic" dir="rtl">${escapeHtml(companyValues['Postal Code'] || '35513')}</td>
      <th class="col2 align-end">الرمز البريدي:</th>
    </tr>
    <tr>
      <th class="col2">VAT Number:</th><td>${escapeHtml(companyValues['VAT Number'] || '314702232800003')}</td>
      <th class="col2">CRN:</th><td colspan="2">${escapeHtml(companyValues.CRN || '7053932963')}</td>
      <th class="col2 align-end">السجل التجاري:</th>
      <td class="arabic" dir="rtl">${escapeHtml(companyValues['VAT Number'] || '314702232800003')}</td>
      <th class="col2 align-end">رقم ضريبة القيمة المضافة:</th>
    </tr>
  `;
  const clientRows = detailsTable([
    ['Name', client.name, transliterateToArabic(client.name), 'الاسم:'],
    ['Address', client.address, transliterateToArabic(client.address), 'العنوان:']
  ]) + `
    <tr>
      <th class="col2">Addl. No:</th><td>${escapeHtml(client.additionalNo)}</td>
      <th class="col2">District:</th><td>${escapeHtml(client.district)}</td>
      <td class="arabic" dir="rtl">${escapeHtml(transliterateToArabic(client.district))}</td>
      <th class="col2 align-end">الحي:</th>
      <td class="arabic" dir="rtl">${escapeHtml(client.additionalNo)}</td>
      <th class="col2 align-end">الرقم الإضافي:</th>
    </tr>
    <tr>
      <th class="col2">Postal Code:</th><td>${escapeHtml(client.postalCode)}</td>
      <th class="col2">Country:</th><td>${escapeHtml(client.country)}</td>
      <td class="arabic" dir="rtl">${arabicCountry(client.country)}</td>
      <th class="col2 align-end">الدولة:</th>
      <td class="arabic" dir="rtl">${escapeHtml(client.postalCode)}</td>
      <th class="col2 align-end">الرمز البريدي:</th>
    </tr>
    <tr>
      <th class="col2">VAT Number:</th><td>${escapeHtml(client.vatNumber)}</td>
      <th class="col2">CRN:</th><td colspan="2">${escapeHtml(client.crn)}</td>
      <th class="col2 align-end">السجل التجاري:</th>
      <td class="arabic" dir="rtl">${escapeHtml(client.vatNumber)}</td>
      <th class="col2 align-end">رقم ضريبة القيمة المضافة:</th>
    </tr>
  `;
  const productRows = products.map((product, index) => `
    <tr>
      <td class="col1">${formatAmount(product.total)}</td>
      <td class="col1">${formatAmount(product.vat)}</td>
      <td class="col1">${escapeHtml(product.vatRate)}%</td>
      <td class="col1">${formatAmount(product.subtotal)}</td>
      <td class="col1">${formatAmount(product.unitRate)}</td>
      <td class="col1">${escapeHtml(product.quantity)}</td>
      <td class="col1">${escapeHtml(product.unit)}</td>
      <td class="colgoods">
        <div>${escapeHtml(product.description)}</div>
        <div class="arabic" dir="rtl">${escapeHtml(transliterateToArabic(product.description))}</div>
      </td>
      <td class="col1">${index + 1}</td>
    </tr>
  `).join('');

  return `<!doctype html>
    <html>
      <head>
        <meta charset="utf-8">
        <title>Quotation ${escapeHtml(quote.quoteNumber)}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; padding: 0; color: #111; }
          table { width: 100%; max-width: 900px; border-collapse: collapse; margin: 14px auto 0; border: 1px solid #d8d2cc; table-layout: fixed; }
          th, td { border: 1px solid #d8d2cc; padding: 4px; text-align: left; height: 20px; line-height: 1.05; font-size: 10px; overflow-wrap: anywhere; }
          th { background-color: #f3f0eb; }
          .header-container { display: flex; justify-content: center; align-items: center; gap: 20px; }
          .header-container h1 { color: #1D2A5F; margin: 0; font-size: 20px; }
          .header-container h2 { color: #D55C36; margin: 5px 0; font-size: 14px; }
          .logo-container img { max-width: 100px; height: auto; }
          .Invoice { text-align: center; color: #1D2A5F; font-size: 18px; font-weight: 700; padding: 8px 0; margin: 0; }
          .clearfix::after { content: ""; display: table; clear: both; }
          .left-align { float: left; }
          .right-align { float: right; text-align: right; }
          .Headtitle td, .new { background-color: #1D2A5F; color: white; font-weight: bold; }
          .Headtitle h3 { margin: 0; padding: 2px; line-height: 1.2; }
          .align-end { text-align: right; direction: rtl; }
          .col2 { width: 12.5%; }
          .col1 { text-align: center; }
          .detail-date th, .detail-date td { width: 20%; text-align: center; height: 30px; }
          .detail-extra th, .detail-extra td { text-align: center; }
          .products th, .products td { text-align: center; padding: 4px 2px; }
          .products th { vertical-align: middle; height: 64px; }
          .products .colgoods { width: 30%; }
          .products .col1 { width: 8%; }
          .arabic { direction: rtl; text-align: right; font-family: Arial, sans-serif; }
          .Summary { width: 65%; margin: 12px 0 0 auto; }
          .Summary .col4 { width: 27%; }
          .Summary .col8 { width: 16%; text-align: center; }
          .Summary .col5 { width: 32%; text-align: right; direction: rtl; }
          .bank-table { margin-top: 12px; table-layout: fixed; }
          .bank-table .new { text-align: center; }
          .signature-section { text-align: center; vertical-align: bottom; height: 120px; padding-bottom: 8px; }
          @media print { * { -webkit-print-color-adjust: exact; print-color-adjust: exact; } body { margin: 10mm; } table { page-break-inside: avoid; } }
        </style>
      </head>
      <body>
        <div class="header-container">
          <div style="text-align:right"><h1>LAR Contracting Company</h1><h2>Quotation | عرض سعر</h2></div>
          <div class="logo-container"><img src="${bill}" alt="LAR Contracting logo" /></div>
        </div>
        <p class="Invoice"><strong>Quotation / عرض سعر</strong></p>
        <div class="clearfix">
          <p class="left-align"><strong>Quote No:</strong> ${escapeHtml(quote.quoteNumber)}</p>
          <p class="right-align" dir="rtl"><strong>رقم عرض السعر:</strong> ${escapeHtml(quote.quoteNumber)}</p>
        </div>
        <table class="Detail">
          <tr class="Headtitle"><td colspan="8"><div class="clearfix"><h3 class="left-align">Our Details:</h3><h3 class="right-align" dir="rtl">تفاصيلنا:</h3></div></td></tr>
          ${companyRows}
        </table>
        <table class="Detail">
          <tr class="Headtitle"><td colspan="8"><div class="clearfix"><h3 class="left-align">Client Details:</h3><h3 class="right-align" dir="rtl">تفاصيل العميل:</h3></div></td></tr>
          ${clientRows}
        </table>
        <table class="Detail detail-date">
          <tr>
            <th>تاريخ الطلب<br>Requested Date</th>
            <th>تاريخ بدء العمل<br>Work Start Date</th>
            <th>رقم العقد / أمر الشراء<br>Contract / PO No</th>
            <th>تاريخ عرض السعر<br>Quote Date</th>
            <th>رقم المشروع / المرجع<br>Project / Reference No</th>
          </tr>
          <tr>
            <td>${escapeHtml(formatDate(quoteDetails.requestedDate))}</td>
            <td>${escapeHtml(formatDate(quoteDetails.workStartDate))}</td>
            <td>${escapeHtml(quoteDetails.contractNo)}</td>
            <td>${escapeHtml(formatDate(quoteDetails.quoteDate))}</td>
            <td>${escapeHtml(quoteDetails.projectRefNo)}</td>
          </tr>
        </table>
        <table class="Detail detail-extra">
          <tr><th>صالح حتى / Valid Until</th><td>${escapeHtml(formatDate(quoteDetails.validUntil))}</td><th>شروط الدفع / Payment Terms</th><td>${escapeHtml(quoteDetails.paymentTerms)}</td></tr>
        </table>
        <table class="Detail products">
          <thead>
            <tr>
              <th>الإجمالي شامل الضريبة<br>Total Incl. VAT (SAR)</th>
              <th>مبلغ ضريبة القيمة المضافة<br>VAT Amount (SAR)</th>
              <th>نسبة الضريبة (%)<br>VAT %</th>
              <th>الإجمالي بدون ضريبة<br>Total Excl. VAT (SAR)</th>
              <th>سعر الوحدة<br>Unit Rate (SAR)</th>
              <th>الكمية<br>Qty / Hours</th>
              <th>وحدة<br>Unit</th>
              <th class="colgoods">طبيعة السلع/الخدمات<br>Nature of Goods/Services (Description)</th>
              <th>#</th>
            </tr>
          </thead>
          <tbody>${productRows}</tbody>
        </table>
        <table class="Summary">
          <tr><td class="col4">Total Excl. VAT (SAR):</td><td class="col8">${formatAmount(totals.subtotal)}</td><td class="col5">الإجمالي بدون ضريبة (ريال سعودي)</td></tr>
          <tr><td class="col4">VAT Amount (SAR):</td><td class="col8">${formatAmount(totals.vat)}</td><td class="col5">مبلغ ضريبة القيمة المضافة (ريال سعودي)</td></tr>
          <tr><td class="col4">Total Incl. VAT (SAR):</td><td class="col8">${formatAmount(totals.total)}</td><td class="col5">الإجمالي شامل الضريبة (ريال سعودي)</td></tr>
          <tr><td class="col4">Balance Due (SAR):</td><td class="col8">${formatAmount(totals.total)}</td><td class="col5">الرصيد المستحق (ر.س)</td></tr>
        </table>
        <table class="bank-table">
          <tr>
            <th colspan="4" class="new">Bank Details / التفاصيل المصرفية</th>
            <th colspan="2" class="new">Prepared By / معد الفاتورة</th>
            <th colspan="2" class="new">Received By / مستلم الفاتورة</th>
          </tr>
          <tr>
            <td colspan="2"><strong>ACCOUNT NAME</strong> / اسم الحساب</td>
            <td colspan="2">LAR Contracting Company<br><span class="arabic">شركة إل آر كونتراكتنج</span></td>
            <td colspan="2" rowspan="4" class="signature-section" style="height:140px;padding:0;"><img src="${stamp}" alt="Signature with stamp" style="width:100%;height:140px;object-fit:fill;display:block;" /></td>
            <td colspan="3" rowspan="4" class="signature-section"><span class="arabic">&nbsp;</span></td>
          </tr>
          <tr><td colspan="2"><strong>BANK</strong> / بنك</td><td colspan="2">Al Rajhi Bank</td></tr>
          <tr><td colspan="2"><strong>ACCOUNT</strong> / الحساب</td><td colspan="2">51300-001-0006085423395</td></tr>
          <tr><td colspan="2"><strong>IBAN</strong> / ايبان</td><td colspan="2">SA5380000513608015423395</td></tr>
        </table>
      </body>
    </html>`;
};
